// frontend/src/pages/crm/Tasks.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { taskAPI } from '../../utils/crmApi';
import {
    ClipboardDocumentListIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    PlusIcon,
    UserGroupIcon,
    DocumentDuplicateIcon,
    EyeIcon,
    EyeSlashIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { EmptyStates } from '../../components/ui/empty-state';

const Tasks = () => {
    const { branding } = useBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const [taskTemplates, setTaskTemplates] = useState([]);
    const [clientTasks, setClientTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    
    // Get client filter from navigation state
    const clientFilter = location.state;
    const [activeTab, setActiveTab] = useState('templates'); // 'templates' or 'assignments'
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });

    useEffect(() => {
        loadData();
    }, [statusFilter, priorityFilter, pagination.page]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Always load task templates
            const templatesResponse = await taskAPI.getTemplates();
            if (templatesResponse.data.success) {
                setTaskTemplates(templatesResponse.data.data);
            }
            
            // Always load client tasks for the count
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(statusFilter && { status: statusFilter }),
                ...(priorityFilter && { priority: priorityFilter }),
                ...(clientFilter?.clientId && { clientId: clientFilter.clientId })
            };
            const tasksResponse = await taskAPI.getAll(params);
            if (tasksResponse.data.success) {
                setClientTasks(tasksResponse.data.data);
                setPagination(tasksResponse.data.pagination);
            }
        } catch (error) {
            console.error('Failed to load data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            not_started: 'bg-muted text-foreground',
            in_progress: 'text-white',
            completed: 'bg-green-100 text-green-700',
            blocked: 'bg-red-100 text-red-700'
        };
        
        const getStatusBgColor = (status) => {
            switch (status) {
                case 'in_progress': return branding.primaryColor;
                case 'completed': return branding.secondaryColor;
                case 'blocked': return branding.accentColor;
                default: return null;
            }
        };
        
        return {
            className: colors[status] || colors.not_started,
            style: status === 'in_progress' ? { backgroundColor: getStatusBgColor(status) } : {}
        };
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'bg-muted text-foreground',
            medium: 'text-white',
            high: 'text-white',
            urgent: 'text-white'
        };
        
        const getPriorityBgColor = (priority) => {
            switch (priority) {
                case 'medium': return branding.accentColor;
                case 'high': return branding.accentColor;
                case 'urgent': return branding.accentColor;
                default: return null;
            }
        };
        
        return {
            className: colors[priority] || colors.medium,
            style: priority !== 'low' ? { backgroundColor: getPriorityBgColor(priority) } : {}
        };
    };

    const calculateProgress = (task) => {
        if (!task.steps || task.steps.length === 0) return 0;
        const completedSteps = task.steps.filter(s => s.status === 'done').length;
        return Math.round((completedSteps / task.steps.length) * 100);
    };

    const isOverdue = (task) => {
        if (!task.dueDate) return false;
        return new Date(task.dueDate) < new Date() && task.status !== 'completed';
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this task template? This will also delete all client assignments.')) return;
        
        try {
            await taskAPI.deleteTemplate(templateId);
            toast.success('Task template deleted successfully');
            loadData();
        } catch (error) {
            console.error('Failed to delete template:', error);
            toast.error('Failed to delete template');
        }
    };

    const handleAssignToClients = async (templateId) => {
        // This would open a modal to select clients
        toast.success('Assignment feature coming soon!');
    };

    return (
        <div className="space-y-6" data-tour="task-system">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">
                        {clientFilter?.clientId ? `Tasks for ${clientFilter.clientName || 'Client'}` : 'Task Management'}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {clientFilter?.clientId 
                            ? `Viewing tasks assigned to ${clientFilter.clientName || 'Client'} (${clientFilter.clientEmail || 'No email'})`
                            : 'Create task templates and assign them to clients'
                        }
                    </p>
                    {clientFilter?.clientId && (
                        <button
                            onClick={() => navigate('/tasks', { replace: true })}
                            className="mt-2 text-sm hover:opacity-80 transition-opacity"
                            style={{ color: branding.primaryColor }}
                        >
                            ← View all tasks
                        </button>
                    )}
                </div>
                <Link
                    to="/task-templates/new"
                    className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{
                        backgroundColor: branding.primaryColor,
                        '--tw-ring-color': branding.primaryColor
                    }}
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Template
                </Link>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('templates')}
                        className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'templates'
                                ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                        }`}
                    >
                        <ClipboardDocumentListIcon className="h-4 w-4" />
                        Task Templates ({taskTemplates.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('assignments')}
                        className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'assignments'
                                ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                        }`}
                    >
                        <UserGroupIcon className="h-4 w-4" />
                        Client Assignments ({clientTasks.length})
                    </button>
                </nav>
            </div>

            {/* Filters */}
            {activeTab === 'assignments' && (
                <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Status Filter */}
                        <div className="relative">
                            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring appearance-none"
                                style={{ '--tw-ring-color': 'var(--brand-primary)' }}
                            >
                                <option value="">All Statuses</option>
                                <option value="not_started">Not Started</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="blocked">Blocked</option>
                            </select>
                        </div>

                        {/* Priority Filter */}
                        <div className="relative">
                            <select
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring appearance-none"
                                style={{ '--tw-ring-color': branding.primaryColor }}
                            >
                                <option value="">All Priorities</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Templates Tab */}
            {activeTab === 'templates' && (
                <div className="bg-card rounded-xl shadow-sm border border-border">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
                        </div>
                    ) : taskTemplates.length === 0 ? (
                        <div className="py-12">
                            <EmptyStates.NoTaskTemplates 
                                onCreateTemplate={() => navigate('/task-templates/new')}
                                branding={branding}
                            />
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {taskTemplates.map((template) => (
                                <div key={template._id} className="p-6 hover:bg-muted">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-base font-semibold text-foreground truncate">
                                                    {template.title}
                                                </h3>
                                                <span 
                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                                                    style={{ 
                                                        backgroundColor: `${branding.primaryColor}20`,
                                                        color: branding?.primaryColor || '#1e40af'
                                                    }}
                                                >
                                                    Template
                                                </span>
                                                <span 
                                                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(template.priority).className}`}
                                                    style={getPriorityColor(template.priority).style}
                                                >
                                                    {template.priority}
                                                </span>
                                            </div>

                                            <p className="text-sm text-muted-foreground mb-3">{template.description}</p>

                                            {/* Template Meta Info */}
                                            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                                                <span className="flex items-center">
                                                    <ClipboardDocumentListIcon className="h-3 w-3 mr-1" />
                                                    {template.steps?.length || 0} steps
                                                </span>
                                                <span className="flex items-center">
                                                    <UserGroupIcon className="h-3 w-3 mr-1" />
                                                    {template.assignedCount || 0} clients assigned
                                                </span>
                                                <span className="flex items-center">
                                                    <ClockIcon className="h-3 w-3 mr-1" />
                                                    {template.estimatedHours || 0}h estimated
                                                </span>
                                                <span>Category: {template.category}</span>
                                            </div>
                                        </div>

                                        {/* Template Actions */}
                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => handleAssignToClients(template._id)}
                                                className="hover:opacity-80 transition-opacity"
                                                style={{ color: branding.secondaryColor }}
                                                title="Assign to Clients"
                                            >
                                                <UserGroupIcon className="h-4 w-4" />
                                            </button>
                                            <Link
                                                to={`/tasks/${template._id}`}
                                                className="hover:opacity-80 transition-opacity"
                                                style={{ color: branding.primaryColor }}
                                                title="View Details"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </Link>
                                            <Link
                                                to={`/tasks/${template._id}/edit`}
                                                className="hover:opacity-80 transition-opacity"
                                                style={{ color: branding.primaryColor }}
                                                title="Edit"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </Link>
                                            <button
                                                onClick={() => handleDeleteTemplate(template._id)}
                                                className="hover:opacity-80 transition-opacity"
                                                style={{ color: branding.accentColor }}
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Client Assignments Tab */}
            {activeTab === 'assignments' && (
                <div className="bg-card rounded-xl shadow-sm border border-border">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
                        </div>
                    ) : clientTasks.length === 0 ? (
                        <div className="text-center py-12">
                            <UserGroupIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                            <h3 className="mt-2 text-sm font-medium text-foreground">No client assignments found</h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Assign task templates to clients to see them here.
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {clientTasks.map((task) => {
                                const progress = calculateProgress(task);
                                const overdue = isOverdue(task);

                                return (
                                    <Link
                                        key={task._id}
                                        to={`/tasks/${task._id}`}
                                        className="block p-6 hover:bg-muted transition-colors"
                                    >
                                        <div className="flex items-start justify-between">
                                            {/* Task Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h3 className="text-base font-semibold text-foreground truncate">
                                                        {task.title}
                                                    </h3>
                                                    {task.visibleToClient ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 flex-shrink-0" title="Visible to client">
                                                            <EyeIcon className="h-3 w-3" />
                                                            Client
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 flex-shrink-0" title="Not visible to client">
                                                            <EyeSlashIcon className="h-3 w-3" />
                                                            Private
                                                        </span>
                                                    )}
                                                    {overdue && (
                                                        <span className="flex items-center text-xs font-medium flex-shrink-0"
                                                            style={{ color: branding.accentColor }}
                                                        >
                                                            <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                                                            Overdue
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Client Info */}
                                                {task.clientId && (
                                                    <p className="text-sm text-muted-foreground mb-3">
                                                        Client: <span className="font-medium">
                                                            {task.clientId.name}
                                                        </span>
                                                    </p>
                                                )}

                                                {/* Progress Bar */}
                                                <div className="mb-3">
                                                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                                        <span>Progress</span>
                                                        <span>{progress}%</span>
                                                    </div>
                                                    <div className="w-full bg-muted rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full transition-all ${
                                                                progress === 100 ? 'text-white' :
                                                                progress >= 50 ? 'text-white' :
                                                                'text-white'
                                                            }`}
                                                            style={{ 
                                                                width: `${progress}%`,
                                                                backgroundColor: progress === 100 ? branding.secondaryColor :
                                                                               progress >= 50 ? branding.primaryColor :
                                                                               branding.accentColor
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Meta Info */}
                                                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                                                    <span>{task.steps?.length || 0} steps</span>
                                                    {task.dueDate && (
                                                        <span className="flex items-center">
                                                            <ClockIcon className="h-3 w-3 mr-1" />
                                                            Due {new Date(task.dueDate).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                    <span>Category: {task.category}</span>
                                                </div>
                                            </div>

                                            {/* Status & Priority Badges */}
                                            <div className="flex flex-col items-end space-y-2 ml-4">
                                                <span 
                                                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status).className}`}
                                                    style={getStatusColor(task.status).style}
                                                >
                                                    {task.status?.replace('_', ' ')}
                                                </span>
                                                <span 
                                                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority).className}`}
                                                    style={getPriorityColor(task.priority).style}
                                                >
                                                    {task.priority}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Pagination - Only for assignments */}
            {activeTab === 'assignments' && pagination.pages > 1 && (
                <div className="bg-background px-4 py-3 flex items-center justify-between border-t border-border sm:px-6 rounded-lg">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-background hover:bg-muted disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                            disabled={pagination.page === pagination.pages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                                <span className="font-medium">{pagination.pages}</span>
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                    disabled={pagination.page === 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-background text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                    disabled={pagination.page === pagination.pages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-background text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tasks;

